const USER_DIRECTION = "incoming";

const asText = value => (typeof value === "string" ? value : "");

const isNonEmpty = text => text.trim().length > 0;

const extractMessageText = message =>
  asText(message?.payload?.text) || asText(message?.payload?.block?.text) || asText(message?.text);

const sortByCreatedAtAsc = messages => {
  if (!messages.every(message => message?.createdAt)) {
    return messages;
  }
  return [...messages].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
};

/**
 * Parse a plain text file: one question per line, blank lines dropped.
 * Splits on \r\n as well as \n so files saved on Windows don't leave a trailing
 * carriage return on every question.
 *
 * @param {string} fileContent
 * @returns {string[]}
 */
export const parseQuestionsFromText = fileContent =>
  asText(fileContent)
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(isNonEmpty);

/**
 * Pull the user's questions out of already-parsed conversation JSON.
 *
 * Understands the shapes a tester can plausibly end up with:
 *   * a downloaded conversation — an array of message objects with `direction`
 *   * the same array wrapped as `{ messages: [...] }`
 *   * an assessment template — `{ omniData: { questions: [{ text }] } }`
 *   * a hand-written array of plain strings
 *
 * @param {*} parsed - The result of JSON.parse on the uploaded file.
 * @returns {string[]}
 */
export const parseQuestionsFromConversationJSON = parsed => {
  const list = Array.isArray(parsed)
    ? parsed
    : Array.isArray(parsed?.messages)
      ? parsed.messages
      : Array.isArray(parsed?.omniData?.questions)
        ? parsed.omniData.questions
        : Array.isArray(parsed?.questions)
          ? parsed.questions
          : null;

  if (list == null) {
    throw new Error(
      "This JSON file isn't a conversation. Expected a list of messages, or a file containing a 'messages' or 'questions' list.",
    );
  }

  if (list.length === 0) {
    throw new Error("This conversation file is empty — there are no messages to replay.");
  }

  if (list.every(item => typeof item === "string")) {
    return list.map(item => item.trim()).filter(isNonEmpty);
  }

  const hasDirection = list.some(item => typeof item?.direction === "string");

  if (!hasDirection) {
    const questionTexts = list.map(item => extractMessageText(item)).filter(isNonEmpty);
    if (questionTexts.length > 0) {
      return questionTexts;
    }
    throw new Error(
      "This JSON file doesn't look like a saved conversation — its messages have no 'direction' or text.",
    );
  }

  const questions = sortByCreatedAtAsc(list)
    .filter(message => message?.direction === USER_DIRECTION)
    .map(extractMessageText)
    .filter(isNonEmpty);

  if (questions.length === 0) {
    throw new Error(
      "No questions found — this conversation has no messages from the user to replay.",
    );
  }

  return questions;
};

/**
 * Parse an uploaded file into the questions to replay.
 *
 * The format is decided by looking at the content rather than the file name, so
 * a conversation download still works if it was renamed or saved without an
 * extension.
 *
 * @param {Object} params
 * @param {string} params.fileName
 * @param {string} params.fileContent
 * @returns {{ questions: string[], format: "json" | "text" }}
 */
export const parseQuestionsFromFile = ({ fileName = "", fileContent = "" }) => {
  const trimmed = asText(fileContent).trim();

  if (trimmed.length === 0) {
    throw new Error("That file is empty. Please choose a file with some content in it.");
  }

  const looksLikeJSON = trimmed.startsWith("{") || trimmed.startsWith("[");
  const namedJSON = fileName.toLowerCase().endsWith(".json");

  if (looksLikeJSON || namedJSON) {
    let parsed;
    try {
      parsed = JSON.parse(trimmed);
    } catch (error) {
      if (namedJSON) {
        throw new Error(`This JSON file couldn't be read: ${error.message}`);
      }
      return { questions: parseQuestionsFromText(trimmed), format: "text" };
    }
    return { questions: parseQuestionsFromConversationJSON(parsed), format: "json" };
  }

  const questions = parseQuestionsFromText(trimmed);
  if (questions.length === 0) {
    throw new Error("No questions found — please use a file with one question per line.");
  }
  return { questions, format: "text" };
};

export default parseQuestionsFromFile;
